import {
  AcceptRequestHistorySchema,
  AcceptRequestResponseEmsEnum,
  AcceptRequestStatusEnum,
  RejectReasonByEmsEnum,
} from 'core/gen/schemas';
import { formatDateTime } from 'core/utils/date';
import { BorderedContainer } from '../../../common/BorderedContainer';
import { SectionTitle } from '../../../common/SectionTitle';
import { useIncidentDetail } from '../../hooks/useIncidentDetail';

type Props = {
  /** 要請履歴。 */
  acceptRequestHistory: ReturnType<
    typeof useIncidentDetail
  >['incidentDetailData']['patientInfo'][number]['acceptRequestHistory'];
};

/** 要請履歴のコンポーネント。 */
export const AcceptRequestHistory = ({ acceptRequestHistory }: Props) => {
  const viewData = fetchResponseAcceptHistoryToViewData(
    acceptRequestHistory ?? []
  );

  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="要請履歴" />
        <div className="flex flex-col gap-[16px]">
          {viewData &&
            viewData.map((item, index) => (
              <div className="flex flex-col gap-[8px]" key={index}>
                <div className="flex flex-wrap gap-[8px] items-center">
                  <p className="text-[14px] noto-sans-jp-bold text-textPrimary">
                    {item.recordedTime}
                  </p>
                  {renderRequestEventStatus(item)}
                </div>
                <div className="flex flex-col gap-[4px]">
                  <p className="text-[14px] noto-sans-jp-medium text-textPrimary">
                    {item.recordedAt}
                  </p>
                </div>
                <hr className="border-textSecondary" />
              </div>
            ))}
        </div>
      </div>
    </BorderedContainer>
  );
};

const REQUEST_EVENT_STATUS_LABELS = {
  [AcceptRequestStatusEnum.UNDER_REQUEST]: '要請開始',
  [AcceptRequestStatusEnum.DESTINATION_DETERMINED]: '搬送先決定',
  [AcceptRequestStatusEnum.DESTINATION_NONE]: 'なし',
  [AcceptRequestStatusEnum.CANCELED]: 'キャンセル',
  [AcceptRequestStatusEnum.TRANSFER]: '転送',
  [AcceptRequestStatusEnum.RESPONSE_BY_EMS]: 'EMS応答',
} as const satisfies Record<keyof typeof AcceptRequestStatusEnum, string>;

const getRequestEventStatusLabel = (requestStatus: AcceptRequestStatusEnum) => {
  if (!requestStatus || !(requestStatus in REQUEST_EVENT_STATUS_LABELS)) {
    return '要請状況不明';
  }

  return REQUEST_EVENT_STATUS_LABELS[requestStatus];
};

const RESPONSE_STATUS_LABELS = {
  [AcceptRequestResponseEmsEnum.ACCEPT]: '受入可',
  [AcceptRequestResponseEmsEnum.REJECT]: '受入不可',
  [AcceptRequestResponseEmsEnum.OTHER]: 'その他',
} as const satisfies Record<keyof typeof AcceptRequestResponseEmsEnum, string>;

const getResponseStatusLabel = (
  responseStatus: AcceptRequestResponseEmsEnum
) => {
  if (!responseStatus || !(responseStatus in RESPONSE_STATUS_LABELS)) {
    return '要請結果不明';
  }

  return RESPONSE_STATUS_LABELS[responseStatus];
};

const REJECT_REASON_BY_EMS_LABELS = {
  [RejectReasonByEmsEnum.FULL_BEDS]: 'ベット満床',
  [RejectReasonByEmsEnum.DOCTOR_ABSENT]: '医師不在',
  [RejectReasonByEmsEnum.NOT_SPECIALIZED]: '専門外',
  [RejectReasonByEmsEnum.OPERATION]: '手術中',
  [RejectReasonByEmsEnum.PATIENT_CARE]: '患者対応中',
  [RejectReasonByEmsEnum.OPERATION_PATIENT_CARE]: '手術中・患者対応中',
  [RejectReasonByEmsEnum.DIFFICULT]: '処置困難',
  [RejectReasonByEmsEnum.FIRST_VISIT]: '初診',
  [RejectReasonByEmsEnum.SELF_REFERRED]: '自己受診',
  [RejectReasonByEmsEnum.UNKNOWN]: '理由不明',
} as const satisfies Record<keyof typeof RejectReasonByEmsEnum, string>;

const getRejectReasonByEmsLabel = (
  rejectReasonByEms: RejectReasonByEmsEnum
) => {
  if (
    !rejectReasonByEms ||
    !(rejectReasonByEms in REJECT_REASON_BY_EMS_LABELS)
  ) {
    return '理由不明';
  }

  return REJECT_REASON_BY_EMS_LABELS[rejectReasonByEms];
};

type AcceptRequestHistoryViewDataItem = {
  recordedTime: string;
  recordedAt: string;
  requestEventStatus: string;
  responseStatus: string;
  rejectReasonByEms: string;
  hospitalName: string;
  destinationHospitalName: string;
};

export const fetchResponseAcceptHistoryToViewData = (
  fetchData: AcceptRequestHistorySchema[]
): AcceptRequestHistoryViewDataItem[] => {
  return fetchData.map(item => ({
    recordedTime: formatDateTime(item.recordedAt, 'HH:mm'),
    recordedAt: formatDateTime(item.recordedAt, 'yyyy/MM/dd HH:mm'),
    requestEventStatus: getRequestEventStatusLabel(item.requestStatus),
    responseStatus: getResponseStatusLabel(
      item.responseStatus ?? AcceptRequestResponseEmsEnum.OTHER
    ),
    rejectReasonByEms: getRejectReasonByEmsLabel(
      item.rejectReasonByEms ?? RejectReasonByEmsEnum.UNKNOWN
    ),
    hospitalName: item.target?.hospitalName ?? '',
    destinationHospitalName: item.destinationTarget?.hospitalName ?? '',
  }));
};

type StatusRenderer = (
  item: AcceptRequestHistoryViewDataItem
) => React.ReactNode;

const requestEventStatusRendererMap: Record<string, StatusRenderer> = {
  搬送先決定: renderDestinationDetermined,
  EMS応答: renderEmsResponse,
};

const renderRequestEventStatus = (item: AcceptRequestHistoryViewDataItem) => {
  const renderer = requestEventStatusRendererMap[item.requestEventStatus];

  if (renderer) {
    return renderer(item);
  }

  return renderDefaultStatus(item);
};

function renderDefaultStatus(item: AcceptRequestHistoryViewDataItem) {
  if (!item.requestEventStatus) {
    return null;
  }

  return (
    <p className="text-[14px] noto-sans-jp-bold text-textPrimary">
      {item.requestEventStatus}
    </p>
  );
}

function renderDestinationDetermined(item: AcceptRequestHistoryViewDataItem) {
  return (
    <>
      {item.destinationHospitalName && (
        <p className="text-[14px] noto-sans-jp-bold text-textPrimary">
          {item.destinationHospitalName}
        </p>
      )}
      <p className="text-[14px] noto-sans-jp-bold text-primary">
        {item.requestEventStatus}
      </p>
    </>
  );
}

function renderEmsResponse(item: AcceptRequestHistoryViewDataItem) {
  return (
    <>
      {item.hospitalName && (
        <p className="text-[14px] noto-sans-jp-bold text-textPrimary">
          {item.hospitalName}
        </p>
      )}
      <p
        className={`text-[14px] noto-sans-jp-bold ${getResponseStatusClassName(
          item.responseStatus
        )}`}
      >
        {item.responseStatus}
      </p>
      {shouldShowRejectReason(item) && (
        <p className="text-[14px] noto-sans-jp-bold text-textPrimary">
          ({item.rejectReasonByEms})
        </p>
      )}
    </>
  );
}

function getResponseStatusClassName(responseStatus: string) {
  switch (responseStatus) {
    case '受入可':
      return 'text-success';
    case '受入不可':
      return 'text-error';
    default:
      return 'text-textPrimary';
  }
}

function shouldShowRejectReason(item: AcceptRequestHistoryViewDataItem) {
  return item.responseStatus === '受入不可' && Boolean(item.rejectReasonByEms);
}
